import { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ListFilter, Trash2 } from "lucide-react";
import { TAT_COLORS, fieldByKey } from "@/lib/fields";

const MONO_COLS = ["sr_no", "ac_ref_no", "ref_no", "pincode", "contact_no", "old_days"];
const DUP_FIELDS = ["ref_no", "candidate_name", "visit_address", "pincode", "contact_no"];
const normVal = (v) => (v === null || v === undefined ? "" : String(v).trim().toLowerCase().replace(/\s+/g, " "));

function HeaderCell({ col, label, sticky, active, onFilter }) {
  const [val, setVal] = useState(active || "");
  const [open, setOpen] = useState(false);
  return (
    <th className={`text-left py-2 px-3 ${sticky ? "sticky left-0 bg-[#F9FAFB] border-r border-[#E5E7EB]" : ""}`}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            className={`flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest transition-colors hover:text-[#2563EB] ${active ? "text-[#2563EB]" : "text-[#4B5563]"}`}
            data-testid={`header-filter-${col}`}>
            {label}
            <ListFilter className={`h-3 w-3 ${active ? "" : "opacity-40"}`} />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-56 p-2 rounded-sm">
          <Input value={val} onChange={(e) => setVal(e.target.value)} placeholder="Filter contains..."
            className="rounded-sm h-8 text-xs mb-2" data-testid={`header-filter-input-${col}`}
            onKeyDown={(e) => e.key === "Enter" && onFilter(col, val)} />
          <div className="flex gap-1">
            <Button size="sm" className="rounded-sm h-7 text-xs flex-1 bg-[#2563EB] hover:bg-[#1D4ED8] text-white" onClick={() => { onFilter(col, val); setOpen(false); }} data-testid={`header-filter-apply-${col}`}>Apply</Button>
            <Button size="sm" variant="ghost" className="rounded-sm h-7 text-xs" onClick={() => { setVal(""); onFilter(col, ""); setOpen(false); }} data-testid={`header-filter-clear-${col}`}>Clear</Button>
          </div>
        </PopoverContent>
      </Popover>
    </th>
  );
}

function CellValue({ col, c }) {
  const v = c[col];
  if (col === "tat") {
    if (!v) return <span className="text-[#9CA3AF]">—</span>;
    return <span className="text-[11px] font-bold text-white px-2 py-0.5 rounded-sm whitespace-nowrap" style={{ backgroundColor: TAT_COLORS[v] }}>{v}</span>;
  }
  if (v === null || v === undefined || v === "") return <span className="text-[#9CA3AF]">—</span>;
  return <span className={MONO_COLS.includes(col) ? "font-mono text-xs" : ""}>{String(v)}</span>;
}

export default function CasesTable({ columns, data, loading, canDelete, selected, onToggleRow, onToggleAll,
  colFilters, onFilter, onOpenCase, onDeleteCase }) {
  const isDup = (col, c) => DUP_FIELDS.includes(col) && (data.duplicates?.[col] || []).includes(normVal(c[col]));
  const colSpan = columns.length + (canDelete ? 2 : 0);
  const dataColumns = useMemo(() => columns.filter((col) => col !== "sr_no"), [columns]);
  return (
    <div className="bg-white border border-[#E5E7EB] overflow-x-auto" data-testid="cases-table-wrapper">
      <table className="w-full text-sm whitespace-nowrap" data-testid="cases-table">
        <thead className="sticky top-0 bg-[#F9FAFB] z-10">
          <tr className="border-b border-[#E5E7EB]">
            {canDelete && (
              <th className="py-2 px-3 w-8">
                <Checkbox
                  checked={data.items.length > 0 && data.items.every((c) => selected.has(c.id))}
                  onCheckedChange={onToggleAll}
                  className="rounded-sm" data-testid="select-all-cases-checkbox" />
              </th>
            )}
            {columns.map((c) => (
              <HeaderCell key={c} col={c} label={c === "sr_no" ? "Sr." : fieldByKey(c)?.label || c}
                sticky={c === "sr_no"} active={colFilters[c]} onFilter={onFilter} />
            ))}
            {canDelete && (
              <th className="py-2 px-3 text-left text-[10px] font-bold uppercase tracking-widest text-[#4B5563]">Actions</th>
            )}
          </tr>
        </thead>
        <tbody>
          {loading && <tr><td colSpan={colSpan} className="py-10 text-center text-[#9CA3AF]">Loading...</td></tr>}
          {!loading && data.items.length === 0 && (
            <tr><td colSpan={colSpan} className="py-10 text-center text-[#9CA3AF]" data-testid="cases-empty">No cases match your filters.</td></tr>
          )}
          {!loading && data.items.map((c) => (
            <tr key={c.id} data-priority-case={c.priority_case === "Yes" ? "true" : undefined}
              className={`border-b transition-colors cursor-pointer ${c.priority_case === "Yes"
                ? "bg-[#D7E3BC] border-[#C9D9AE] hover:bg-[#CCDCB2]"
                : "border-[#F3F4F6] hover:bg-[#F3F4F6]"}`}
              onClick={() => onOpenCase(c.id)} data-testid={`case-row-${c.id}`}>
              {canDelete && (
                <td className="py-2 px-3" onClick={(e) => e.stopPropagation()}>
                  <Checkbox checked={selected.has(c.id)} className="rounded-sm" data-testid={`select-case-${c.id}`}
                    onCheckedChange={(v) => onToggleRow(c.id, v)} />
                </td>
              )}
              <td className={`sticky left-0 border-r border-[#E5E7EB] py-2 px-3 font-mono text-xs font-bold ${c.priority_case === "Yes" ? "bg-[#D7E3BC]" : "bg-white"}`}>{c.sr_no ?? "—"}</td>
              {dataColumns.map((col) => (
                <td key={col} data-testid={`cell-${col}-${c.id}`}
                  className={`py-2 px-3 max-w-[220px] truncate ${isDup(col, c) ? "bg-[#FBCFE8] text-[#9D174D] font-semibold" : ""}`}>
                  <CellValue col={col} c={c} />
                </td>
              ))}
              {canDelete && (
                <td className="py-2 px-3">
                  <button onClick={(e) => onDeleteCase(e, c.id)} className="text-[#9CA3AF] hover:text-[#EF4444] transition-colors"
                    data-testid={`delete-case-${c.id}`}><Trash2 className="h-4 w-4" /></button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
